import { completePartialJson, pruneFlatElementSpec } from '@/lib/partialJson';

/**
 * Progressive parsing for the in-chat UI-generation tools.
 *
 * While the agent writes a `render_custom_view` / `render_chart` tool call we
 * receive the accumulating JSON text of the tool input (via `tool-input-delta`
 * events). These helpers turn that truncated text into something renderable so
 * the view/chart can build up live, mirroring the email-template editor. The
 * final `tool-result` (rendered from `msg.toolResults`) remains the source of
 * truth and replaces whatever partial state was shown.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export interface PartialInternalUiView {
  spec: Record<string, unknown>;
  state: Record<string, unknown>;
  title?: string;
}

/**
 * Parse the partial `render_custom_view` tool input — shape
 * `{ spec: { root, elements }, state?, title? }` — into a pruned, renderable
 * internal-UI view. Returns null until a root element is present.
 */
export function parsePartialInternalUiView(
  partialInput: string,
): PartialInternalUiView | null {
  const completed = completePartialJson(partialInput);
  if (!completed) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(completed);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || !isRecord(parsed.spec)) return null;

  const spec = pruneFlatElementSpec(parsed.spec);
  if (!spec) return null;

  return {
    spec,
    state: isRecord(parsed.state) ? parsed.state : {},
    title: typeof parsed.title === 'string' ? parsed.title : undefined,
  };
}

export interface PartialChartSpec {
  /** Inner Vega-Lite spec as a JSON string, fed to `parseChartSpec`. */
  spec: string;
  title?: string;
}

/**
 * Parse the partial `render_chart` tool input — shape
 * `{ spec: "<vega-lite json string>", title? }`. The `spec` is itself a JSON
 * string, so we complete the outer tool input, then complete the inner spec
 * string. The caller still runs `parseChartSpec` and only renders once that
 * validates — a Vega-Lite spec only draws when it is reasonably complete.
 *
 * Note: because `spec` is a *string*, while the model is mid-writing it the
 * outer value is an unterminated string literal that cannot be safely
 * salvaged, so this returns null until the string closes (the `tool-input-end`
 * flush, just before the call). Standalone charts therefore preview at the
 * last moment rather than building up; charts *embedded in a custom view* are
 * nested objects and do stream progressively. This is acceptable — Vega only
 * renders a near-complete spec anyway.
 */
export function parsePartialChartSpec(
  partialInput: string,
): PartialChartSpec | null {
  const completedOuter = completePartialJson(partialInput);
  if (!completedOuter) return null;

  let outer: unknown;
  try {
    outer = JSON.parse(completedOuter);
  } catch {
    return null;
  }
  if (!isRecord(outer) || typeof outer.spec !== 'string') return null;

  const innerSpec = completePartialJson(outer.spec);
  if (!innerSpec) return null;

  return {
    spec: innerSpec,
    title: typeof outer.title === 'string' ? outer.title : undefined,
  };
}
